import { useMemo } from 'react';
import { BloodPressureItem } from 'models/bloodpressure';
import { t } from 'i18next';
import { calculateAverage } from 'utils/helpers';
import { formatDateHighcharts, getHighchartsDateTimeLabelFormats } from 'utils/dates';
import { useAppContext, usePatientDetailContext } from 'context/hooks';
import { Point } from 'highcharts';
import { renderYesNoValue } from 'utils/general';
import dayjs from 'dayjs';
import { chartTooltip } from './ChartTooltip';

interface BloodPressureChartOptionsProps {
  data: BloodPressureItem[];
  onClickData?: (date: number) => void;
  options?: Omit<Highcharts.Options, 'series' | 'loading'>;
}

/* Chart specific constants */
const SYSTOLIC_RANGE = { from: 90, to: 134 };
const DIASTOLIC_RANGE = { from: 50, to: 84 };

export const BloodPressureChartOptions = ({ data, onClickData, options }: BloodPressureChartOptionsProps) => {
  const { currentUser } = useAppContext();
  const { startDate, endDate } = usePatientDetailContext();
  const systolicAverage = calculateAverage(data.map((value) => parseInt(value.bloodPressure.split('/')[0], 10)));
  const diastolicAverage = calculateAverage(data.map((value) => parseInt(value.bloodPressure.split('/')[1], 10)));

  const mappedData: [number[], number[]] = useMemo(() => {
    return data.reduce(
      (prev, curr) => {
        const updated = prev as any;
        const [sys, dia] = curr.bloodPressure.split('/');
        const { date } = curr;

        updated[0].push([date, parseInt(sys, 10)]);
        updated[1].push([date, parseInt(dia, 10)]);

        return updated;
      },
      [[], []],
    );
  }, [data]);

  const onClickTooltip = (event: Highcharts.SeriesClickEventObject) => {
    if (onClickData) {
      onClickData(event.point.x);
    }
  };

  const createSeries = (seriesData: { name: string; color: string }[]): Highcharts.SeriesOptionsType[] => {
    return seriesData.map((item, i) => ({
      color: item.color,
      name: item.name,
      type: 'line',
      data: mappedData[i],
      cursor: onClickData ? 'pointer' : undefined,
      events: {
        click: onClickTooltip,
      },
      marker: {
        symbol: 'circle',
      },
      zIndex: 5,
    }));
  };

  return {
    title: undefined,
    chart: {
      height: 350,
    },
    ...options,
    tooltip: {
      shared: true,
      useHTML: true,
      formatter(this: Point & { points: Point[] }): string {
        const { points } = this;
        if (!points) return t('na_short_label');

        const point = points[0].x;

        const dataItem = data.find((item) => item.date === point);

        const tooltipData = [
          {
            label: t(`blood_pressure_sys_dia_label`),
            value: `${points[0].y} / ${points[1].y}`,
          },
          {
            label: t(`severity_label`),
            value: t(`severity_${dataItem?.classification?.label}`),
          },
          {
            label: t('symptoms_label'),
            value: renderYesNoValue(dataItem?.symptoms[0] !== undefined),
          },
          {
            label: t('pulse_pressure_label'),
            value: dataItem?.pulsePressure.toString() || t('na_short_label'),
          },
          {
            label: t(`comment_label`),
            value: renderYesNoValue(dataItem?.commentCount !== undefined),
          },
          {
            label: t('attachment_label'),
            value: renderYesNoValue(dataItem?.attachment !== undefined),
          },
        ];

        return chartTooltip({
          date: formatDateHighcharts(point, currentUser?.userConfiguration.data.hourFormat, true),
          classification: dataItem?.classification,
          tooltipData,
        });
      },
      ...options?.tooltip,
    },
    legend: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: 'mmHg',
      },
      gridZIndex: 3,
      zIndex: 3,
      ...options?.yAxis,
      plotBands: [
        {
          color: 'var(--mantine-color-turquoise-1',
          ...SYSTOLIC_RANGE,
          zIndex: 1,
        },
        {
          color: 'var(--mantine-color-turquoise-3)',
          ...DIASTOLIC_RANGE,
          zIndex: 1,
        },
      ],
      plotLines: [
        {
          color: 'var(--mantine-color-red-5)',
          value: systolicAverage,
          zIndex: 3,
          label: {
            text: `${Math.floor(systolicAverage)}`,
            textAlign: 'center',
            align: 'right',
            x: -10,
            style: {
              color: 'var(--mantine-color-red-5)',
            },
          },
        },
        {
          color: 'var(--mantine-color-red-5)',
          value: diastolicAverage,
          zIndex: 3,
          label: {
            text: `${Math.floor(diastolicAverage)}`,
            textAlign: 'center',
            align: 'right',
            x: -10,
            style: {
              color: 'var(--mantine-color-red-5)',
            },
          },
        },
      ],
    },
    xAxis: {
      ...options?.xAxis,
      type: 'datetime',
      min: dayjs(startDate).unix() * 1000,
      max: dayjs(endDate).unix() * 1000,
      dateTimeLabelFormats: getHighchartsDateTimeLabelFormats(currentUser?.userConfiguration.data.hourFormat),
    },
    series: createSeries([
      { name: t('systolic_label'), color: 'var(--mantine-color-blue-7)' },
      { name: t('diastolic_label'), color: 'var(--mantine-color-blue-5)' },
    ]),
    credits: {
      enabled: false,
    },
    accessibility: {
      enabled: false,
    },
  };
};
